import { Injectable } from '@angular/core';

/**
 * Service pour sauvegarder temporairement les valeurs des inputs de commentaires
 * pendant que l'utilisateur tape, afin de les restaurer si le formulaire se recharge
 */
@Injectable({
  providedIn: 'root'
})
export class InputPersistenceService {

  // Map pour stocker les valeurs temporaires des inputs
  // Clé format: questionKey_rowKey_statementKey
  private temporaryValues = new Map<string, string>();

  constructor() { }

  /**
   * Génère une clé unique pour identifier un input de commentaire
   */
  private generateKey(questionKey: string, statementKey: string, rowKey?: string): string {
    if (rowKey) {
      return `${questionKey}_${rowKey}_${statementKey}`;
    }
    return `${questionKey}_${statementKey}`;
  }

  /**
   * Sauvegarde la valeur actuelle d'un input (appelé à chaque frappe)
   */
  saveValue(questionKey: string, value: string, statementKey: string, rowKey?: string): void {
    const key = this.generateKey(questionKey, statementKey, rowKey);
    if (value && value.trim().length > 0) {
      this.temporaryValues.set(key, value);
    } else {
      // Si la valeur est vide, on la retire du cache
      this.temporaryValues.delete(key);
    }
  }

  /**
   * Récupère la valeur sauvegardée d'un input (appelé au ngOnInit)
   */
  getValue(questionKey: string, statementKey: string, rowKey?: string): string | null {
    const key = this.generateKey(questionKey, statementKey, rowKey);
    const value = this.temporaryValues.get(key) || null;
    return value;
  }

  /**
   * Supprime la valeur sauvegardée (appelé au onBlur lors de la validation)
   */
  clearValue(questionKey: string, statementKey: string, rowKey?: string): void {
    const key = this.generateKey(questionKey, statementKey, rowKey);
    this.temporaryValues.delete(key);
  }

  /**
   * Supprime toutes les valeurs sauvegardées pour une question donnée
   */
  clearQuestion(questionKey: string): void {
    const keysToDelete: string[] = [];
    this.temporaryValues.forEach((_, key) => {
      if (key.startsWith(`${questionKey}_`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.temporaryValues.delete(key));
  }

  /**
   * Supprime toutes les valeurs sauvegardées (appelé lors de la soumission du sondage)
   */
  clearAll(): void {
    this.temporaryValues.clear();
  }
}

